/*
 *  Links List - Create a list of links, and then share it!
 *  Copyright (c) Luke Denton
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation; either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/auth';
import store from '@store';
import appConfig from "@/application-configuration";
import tables from '../../functions/tables.json';
import {LIST_PERMISSION__LOCKED, LIST_PERMISSION__PUBLIC} from "@constants";
import Analytics from "@utils/Analytics";

firebase.initializeApp(appConfig.firebase);

export const auth = firebase.auth();
export const db = firebase.database();

let linksRef, metaRef, uiRef, permissionRef;
let listeningToLists = false;
let listeningToBookmarks = false;

export const listenForListChange = async (urlString) => {
  // Handle checking if a list exists in either the new or old location (and migrating if in old)
  const snapshot = await db.ref(`/${tables.lists2}/${urlString}/id`).once('value');
  const exists = snapshot.exists();
  if (!exists) {
    // No longer automatically migrating the list, because that opens me up to full database reads!
    // Users will instead have to contact me to manually migrate the list data
    store.dispatch('setExists', { exists: false })
    return;
  }

  // At this point, we've established that the list exists within the "new" location (lists2)

  // Listen for children added to links
  linksRef = db.ref(`/${tables.lists2}/${urlString}/links`);
  linksRef.on('child_added', (snapshot) => {
    const addedLink = snapshot.val();
    store.dispatch('receiveAddLink', addedLink);
    store.dispatch('trackNextLinkId');

    // Going to set loading to false everytime that a child update is received but the idea being that after the first child
    // is received, this will be set to false, because there is at least a bit of data to render
    store.dispatch('setAreLinksLoading', { isLoading: false });
  }, (e) => {
    if (e.toString().includes('permission_denied')) {
      // @TODO Update the app to display a message indicating that a user doesn't have permission - part of private lists ticket
      console.log(`No permission`);
      linksRef.off();
    }
  })

  // Listen for children removed from links
  linksRef.on('child_removed', (snapshot) => {
    const removedLink = snapshot.val();
    store.dispatch('receiveRemoveLink', { id: removedLink.id });
  }, (e) => {
    if (e.toString().includes('permission_denied')) {
      // @TODO Update the app to display a message indicating that a user doesn't have permission - part of private lists ticket
      console.log(`No permission`);
      linksRef.off();
    }
  })

  // Listen to list meta changing
  metaRef = db.ref(`/${tables.lists2}/${urlString}/meta`);
  metaRef.on('value', (snapshot) => {
    const metaValue = snapshot.val();
    store.dispatch('userInput/setMeta', metaValue);
  }, (e) => {
    if (e.toString().includes('permission_denied')) {
      // @TODO Update the app to display a message indicating that a user doesn't have permission - part of private lists ticket
      console.log(`No permission`);
      metaRef.off();
    }
  })

  // Listen to list theme changing
  uiRef = db.ref(`/${tables.lists2}/${urlString}/ui`);
  uiRef.on('value', (snapshot) => {
    const themeValue = snapshot.val();
    store.dispatch('ui/setUi', themeValue);
  }, (e) => {
    if (e.toString().includes('permission_denied')) {
      // @TODO Update the app to display a message indicating that a user doesn't have permission - part of private lists ticket
      console.log(`No permission`);
      uiRef.off();
    }
  })
  // Listen to list permission changing (i.e. becoming locked)
  permissionRef = db.ref(`/${tables.lists2}/${urlString}/isLocked`);
  permissionRef.on('value', (snapshot) => {
    const isLocked = snapshot.val();
    store.dispatch('setListPermission', { permission: isLocked ? LIST_PERMISSION__LOCKED : LIST_PERMISSION__PUBLIC})
  }, (e) => {
    if (e.toString().includes('permission_denied')) {
      // @TODO Update the app to display a message indicating that a user doesn't have permission - part of private lists ticket
      console.log(`No permission`);
      permissionRef.off();
    }
  })
};

export const stopListeningForListChange = () => {
  if (linksRef) linksRef.off();
  if (metaRef) metaRef.off();
  if (uiRef) uiRef.off();
  if (permissionRef) permissionRef.off();
}

export const listenForLists = (userId) => {
  return new Promise((res) => {
    if (listeningToLists) {
      res();
      return;
    }

    listeningToLists = true;
    // There are saved lists, so set up listeners to handle when a list is subscribed to, but more importantly when an
    // list is unsubscribed from
    db.ref(`/${tables.users}/${userId}/myLists`).on('child_added', (snapshot) => {
      const firebaseId = snapshot.key;
      const urlString = snapshot.val();

      db.ref(`/${tables.lists2}/${urlString}`).once('value', (snapshot) => {
        if (!snapshot.exists()) {
          Analytics.FireEvent('old-list', {
            event_category: 'database_tracker',
            event_label: 'myLists'
          });
          store.dispatch('addToMyLists', { firebaseId, list: { id: urlString, needsMigrating: true } });
          return;
        }

        // List does exist in the new location
        const list = snapshot.val();
        store.dispatch('addToMyLists', { firebaseId, list });
      });
    });

    // * An owned list can't be removed at this stage, so no need to listen for it

    // This will be called sooner than lists are being displayed, because the above is async, and because we're listening
    // for multiple children being added, not just 1 value, then there's no way of knowing when everything has loaded
    setTimeout(() => {
      // At least avoid the "you have no lists" message if there are lists
      // Race condition, but if loading from Firebase takes longer than this, then meh
      res();
    }, 500)
  })
}

export const listenForBookmarks = (userId) => {
  return new Promise((res) => {
    if (listeningToBookmarks) {
      res();
      return;
    }

    listeningToBookmarks = true;

    // There are saved lists, so set up listeners to handle when a list is subscribed to, but more importantly when an
    // list is unsubscribed from
    db.ref(`/${tables.users}/${userId}/subscribedLists`).on('child_added', (snapshot) => {
      const firebaseId = snapshot.key;
      const urlString = snapshot.val();

      db.ref(`/${tables.lists2}/${urlString}`).once('value', (snapshot) => {
        if (!snapshot.exists()) {
          Analytics.FireEvent('old-list', {
            event_category: 'database_tracker',
            event_label: 'myLists'
          });

          store.dispatch('addSubscribedList', { firebaseId, list: { id: urlString, needsMigrating: true } });
          return;
        }

        const list = snapshot.val();
        store.dispatch('addSubscribedList', { firebaseId, list });
      });
    });

    // Listen for bookmarks being removed
    db.ref(`/${tables.users}/${userId}/subscribedLists`).on('child_removed', (snapshot) => {
      const id = snapshot.val();
      store.dispatch('removeSubscribedList', { id });
    });

    // This will be called sooner than lists are being displayed, because the above is async, and because we're listening
    // for multiple children being added, not just 1 value, then there's no way of knowing when everything has loaded
    setTimeout(() => {
      // At least avoid the "you have no lists" message if possible
      // Race condition, but if loading from Firebase takes longer than this, then meh
      res();
    }, 500)
  })
}

